import { useLingui } from "@lingui/react/macro";
import { LIVE_DEFAULT_VOICE_ID, LIVE_VOICES } from "@rakazo/contracts";
import { Button, toast } from "@rakazo/ui-web";
import { Phone } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ArtifactTarget } from "../../lib/artifact-open";
import { authClient } from "../../lib/auth";
import { endCall } from "../../lib/call-session";
import { getActiveUiLocale } from "../../lib/i18n";
import type { LiveToolResult, LiveVoiceSnapshot, LiveVoiceStatus } from "../../lib/live-voice";
import {
  initialLiveSnapshot,
  LIVE_SYSTEM_INSTRUCTION,
  LiveVoiceSession,
} from "../../lib/live-voice";
import { BrowserLiveAudio } from "../../lib/live-voice-audio";
import { createLiveToolBroker } from "../../lib/live-voice-broker";
import { liveVoiceReference } from "../../lib/live-voice-context";
import type { LiveInputReceipt } from "../../lib/live-voice-input";
import { getLiveInputOutbox } from "../../lib/live-voice-input";
import { sendLiveVoiceInput } from "../../lib/live-voice-input-rpc";
import { backgroundsWhenHidden, bindLiveVoicePage } from "../../lib/live-voice-page";
import { liveVoiceForBot } from "../../lib/live-voice-preference";
import { holdLiveVoiceWork, setLiveVoiceActive } from "../../lib/live-voice-presence";
import type { LiveHistoryStatus } from "../../lib/live-voice-recording";
import {
  flushLiveRecords,
  forgetLiveRecorder,
  LiveTranscriptRecorder,
  liveRecordScope,
  registerLiveRecorder,
} from "../../lib/live-voice-recording";
import { loadLiveStatus, requestLiveToken } from "../../lib/live-voice-rpc";
import { LiveVoiceTiming } from "../../lib/live-voice-timing";
import { createSpaceRpc, selectedSpaceId } from "../../lib/rpc";
import { IconTooltip } from "../IconTooltip";
import type { LiveUnavailableReason } from "./LiveUnavailableNotice";
import { LiveUnavailableNotice } from "./LiveUnavailableNotice";
import { LiveVoiceOverlay } from "./LiveVoiceOverlay";

function microphonePermissionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    [
      "NotAllowedError",
      "PermissionDeniedError",
      "NotFoundError",
      "NotReadableError",
      "SecurityError",
    ].includes(error.name) || /microphone|permission/i.test(error.message)
  );
}

function finishVoiceInput(session: LiveVoiceSession): void {
  const release = holdLiveVoiceWork();
  void (async () => {
    try {
      await session.finishInput();
    } finally {
      try {
        session.dispose();
      } finally {
        release();
      }
    }
  })().catch(() => undefined);
}

/**
 * The live control stays visible in a direct chat. A key or a server that is off is
 * not a reason to hide it: clicking opens the shared setup dialog, which explains the
 * state and asks for the person's own Google key when one is all that is missing.
 */
export function LiveConversation({
  target,
  botName,
  botTitle,
  onOpenModels,
  onOpenVoice,
  onSetup,
  disabled,
}: {
  target: ArtifactTarget;
  botName?: string;
  botTitle?: string;
  onOpenModels: () => void;
  onOpenVoice?: () => void;
  /** Opens the key dialog when live has no Google credential yet. */
  onSetup?: () => void;
  disabled?: boolean;
}) {
  const { t } = useLingui();
  const userSession = authClient.useSession();
  const recordScope = userSession.data
    ? JSON.stringify([
        window.location.origin,
        userSession.data.user.id,
        userSession.data.session.id,
      ])
    : null;
  const voiceId = "botId" in target ? liveVoiceForBot(target.botId) : LIVE_DEFAULT_VOICE_ID;
  const selectedVoice = LIVE_VOICES.find((voice) => voice.id === voiceId) ?? LIVE_VOICES[0];
  const systemInstruction = [
    LIVE_SYSTEM_INSTRUCTION,
    `You are speaking as ${botName ?? "this HrokBot bot"}${botTitle ? ` (${botTitle})` : ""}.`,
    `Your live voice is ${selectedVoice.gender}. When the person's language uses grammatical gender, refer to yourself in the ${selectedVoice.gender === "male" ? "masculine" : "feminine"} gender. This does not select the conversation language.`,
  ].join("\n");
  const [status, setStatus] = useState<LiveVoiceStatus | null | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<LiveUnavailableReason | null>(null);
  const [snapshot, setSnapshot] = useState<LiveVoiceSnapshot>(() => initialLiveSnapshot());
  const recorderRef = useRef<LiveTranscriptRecorder | null>(null);
  const [historyStatus, setHistoryStatus] = useState<LiveHistoryStatus>("saved");
  const sessionRef = useRef<LiveVoiceSession | null>(null);
  const taskWaitRef = useRef<AbortController | null>(null);
  const audioRef = useRef<BrowserLiveAudio | null>(null);
  const alive = useRef(true);
  const startAttempt = useRef(0);

  useEffect(() => {
    alive.current = true;
    liveRecordScope(recordScope);
    void loadLiveStatus(voiceId)
      .then((next) => {
        if (alive.current) setStatus(next);
      })
      .catch(() => {
        if (alive.current) setStatus(null);
      });
    return () => {
      alive.current = false;
      taskWaitRef.current?.abort();
      taskWaitRef.current = null;
      const session = sessionRef.current;
      sessionRef.current = null;
      const audio = audioRef.current;
      audioRef.current = null;
      if (session) finishVoiceInput(session);
      else audio?.close();
    };
  }, [voiceId, recordScope]);

  useEffect(() => {
    setLiveVoiceActive(open);
    if (open) endCall();
    return () => {
      if (open) setLiveVoiceActive(false);
    };
  }, [open]);

  const liveReady = status?.enabled === true && status.hasGoogleKey;
  const keepAwake = open && snapshot.phase !== "error" && snapshot.phase !== "ended";
  useEffect(() => {
    if (!keepAwake) return;
    return bindLiveVoicePage((hidden) =>
      sessionRef.current?.setBackgrounded(hidden && backgroundsWhenHidden()),
    );
  }, [keepAwake]);

  const start = useCallback(
    async (ready = liveReady) => {
      if (disabled || starting || !ready) return;
      const attempt = ++startAttempt.current;
      const initialSpaceId = selectedSpaceId();
      const initialRpc = createSpaceRpc(initialSpaceId);
      const timing = new LiveVoiceTiming();
      const audio = audioRef.current ?? new BrowserLiveAudio();
      audioRef.current = audio;
      setStarting(true);
      try {
        // Unlock audio in the click's gesture before the token round-trip.
        await audio.prepare();
        timing.mark("audio_prepared");
      } catch (error) {
        if (alive.current && attempt === startAttempt.current) {
          setStarting(false);
          setNotice(microphonePermissionError(error) ? "permission" : "error");
        }
        return;
      }
      if (!alive.current || attempt !== startAttempt.current) return;
      try {
        const voice = await initialRpc.voice.status(undefined, {
          signal: AbortSignal.timeout(8_000),
        });
        if (!alive.current || attempt !== startAttempt.current) return;
        if (!voice.transcribe) {
          setNotice("no_transcriber");
          return;
        }
        if (!recordScope)
          throw Error("A current signed-in session is required / Потрібна чинна сесія входу");
        await flushLiveRecords(recordScope, JSON.stringify([initialSpaceId, target]));
        setOpen(true);
        setSnapshot(initialLiveSnapshot("connecting"));
        const [token, initialContext] = await Promise.all([
          requestLiveToken(voiceId).then((value) => {
            if (value) timing.mark("token_ready");
            return value;
          }),
          initialRpc.live.context(target, { signal: AbortSignal.timeout(8_000) }).then((value) => {
            timing.mark("context_ready");
            return value;
          }),
        ]);
        if (!alive.current || attempt !== startAttempt.current) return;
        if (!token) {
          setOpen(false);
          setNotice("error");
          return;
        }
        const spaceId = initialContext.spaceId ?? initialSpaceId;
        if (!spaceId)
          throw Error("A current space is required / Потрібен визначений поточний простір");
        const sessionRpc = createSpaceRpc(spaceId);
        const recordTarget = JSON.stringify([spaceId, target]);
        let context = initialContext;
        if (spaceId !== initialSpaceId) {
          await flushLiveRecords(recordScope, recordTarget);
          context = await sessionRpc.live.context(target, { signal: AbortSignal.timeout(8_000) });
        }
        if (!alive.current || attempt !== startAttempt.current) return;
        const callId = crypto.randomUUID();
        const recorder = new LiveTranscriptRecorder(
          callId,
          (record, signal) => sessionRpc.live.record({ ...target, ...record }, { signal }),
          (next) => {
            if (next === "saved") forgetLiveRecorder(callId, recorder);
            const current = alive.current && recorderRef.current === recorder;
            if (current) setHistoryStatus(next);
            if (next === "error") {
              if (current) sessionRef.current?.setMuted(true);
              toast.error(t`Voice history was not saved. Retry saving before continuing.`, {
                id: `voice-history-${callId}`,
                action: {
                  label: t`Retry saving`,
                  onClick: () => {
                    void recorder.retry().catch(() => undefined);
                  },
                },
              });
            }
          },
        );
        recorderRef.current = recorder;
        const taskWait = new AbortController();
        taskWaitRef.current = taskWait;
        const watchedRuns = new Set<string>();
        const inputOutbox = getLiveInputOutbox({
          scope: recordScope,
          send: (input) => sendLiveVoiceInput(input, recordScope),
        });
        const broker = createLiveToolBroker(target, {
          automaticInput: true,
          callId,
          timing,
          beforeRecall: () => recorder.flush(),
          previousCallIds: context.conversations?.map((call) => call.callId),
          send: (input) => sessionRpc.live.send(input),
          read: (input, signal) => sessionRpc.live.task(input, signal ? { signal } : undefined),
          subscribe: (input, cursor, signal) =>
            sessionRpc.threads.subscribe({ ...input, cursor }, { signal }),
          cancel: (input, signal) => sessionRpc.live.cancel(input, signal ? { signal } : undefined),
          recall: (input, signal) => sessionRpc.live.recall(input, signal ? { signal } : undefined),
        });
        const session = new LiveVoiceSession({
          token,
          audio,
          timing,
          broker,
          refreshToken: () => requestLiveToken(voiceId),
          systemInstruction: `${systemInstruction}\n${liveVoiceReference(context)}\nCurrent connection time (UTC): ${new Date().toISOString()}. This timestamp is not a source for external facts.`,
          greetingLanguage: getActiveUiLocale(),
          onSnapshot: (next) => {
            if (alive.current && sessionRef.current === session) setSnapshot(next);
          },
          onTranscriptTurn: (turn) => {
            try {
              registerLiveRecorder(recordScope, recordTarget, recorder, callId);
              recorder.enqueue(turn);
            } catch {
              if (alive.current) setHistoryStatus("error");
              session.setMuted(true);
              toast.error(t`Voice history was not saved. Retry saving before continuing.`);
            }
          },
          onUserAudioUtterance: async (utterance) => {
            const release = holdLiveVoiceWork();
            let receipt: LiveInputReceipt | null;
            try {
              receipt = await inputOutbox.enqueue({
                spaceId,
                target,
                callId,
                utteranceId: utterance.utteranceId,
                audioBase64: audioBase64(utterance.audio),
                mimeType: utterance.mimeType,
              });
            } finally {
              release();
            }
            // No receipt means the server heard no speech: there is no bot task to follow.
            if (!receipt || taskWait.signal.aborted || watchedRuns.has(receipt.runId)) return;
            watchedRuns.add(receipt.runId);
            const deliver = (result: LiveToolResult) => {
              if (!taskWait.signal.aborted) session.deliverAgentResult(result);
            };
            void broker
              .followInput({ ...receipt, signal: taskWait.signal, onProgress: deliver })
              .then(deliver)
              .catch(() => undefined)
              .finally(() => watchedRuns.delete(receipt.runId));
          },
          onIncompleteUserUtterance: (input) => {
            // An abandoned capture (reconnect, Mute tap, hidden window) is not a fault.
            if (input.reason !== "unfinished") session.setMuted(true);
            if (alive.current && input.reason !== "delivery_failed")
              toast.error(t`Voice request is incomplete. Please send it in chat.`);
          },
        });
        sessionRef.current = session;
        session.start();
        session.setBackgrounded(document.visibilityState !== "visible" && backgroundsWhenHidden());
      } catch {
        if (alive.current && attempt === startAttempt.current) {
          setOpen(false);
          setNotice("error");
        }
      } finally {
        if (alive.current && attempt === startAttempt.current) setStarting(false);
      }
    },
    [
      disabled,
      liveReady,
      starting,
      systemInstruction,
      target,
      voiceId,
      recordScope,
      t,
      onOpenVoice,
      onSetup,
    ],
  );

  const end = useCallback(() => {
    startAttempt.current += 1;
    taskWaitRef.current?.abort();
    taskWaitRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    audioRef.current = null;
    if (session) finishVoiceInput(session);
    setStarting(false);
    setOpen(false);
    setSnapshot(initialLiveSnapshot("ended"));
  }, []);

  const retry = useCallback(() => {
    const session = sessionRef.current;
    if (session) void session.resume();
    else void start(true);
  }, [start]);

  async function onControlClick() {
    if (sessionRef.current) {
      setOpen(true);
      return;
    }
    // The key may have been saved in the bot settings while this control stayed mounted.
    const next = await loadLiveStatus(voiceId).catch(() => null);
    if (next) setStatus(next);
    if (next?.enabled === true && next.hasGoogleKey) {
      void start(true);
      return;
    }
    if (!next && liveReady) {
      void start(true);
      return;
    }
    if (onSetup) {
      onSetup();
      return;
    }
    setNotice(
      next && !next.enabled ? "disabled" : next && !next.hasGoogleKey ? "no_key" : "unavailable",
    );
  }

  return (
    <>
      <IconTooltip label={t`Live conversation`}>
        <Button
          variant="outline"
          size="icon"
          aria-label={t`Live conversation`}
          aria-haspopup="dialog"
          aria-busy={starting}
          disabled={disabled || starting}
          onClick={() => void onControlClick()}
          className="app-no-drag size-11 shrink-0 rounded-full border-0 bg-transparent text-foreground/75 shadow-none hover:bg-muted"
        >
          <Phone size={16} strokeWidth={1.8} />
        </Button>
      </IconTooltip>

      {open
        ? createPortal(
            <LiveVoiceOverlay
              snapshot={snapshot}
              botName={botName}
              onEnd={end}
              onRetry={retry}
              onToggleMute={() => sessionRef.current?.toggleMute()}
              onBackToText={end}
              historyStatus={historyStatus}
              onRetryHistory={() => {
                void recorderRef.current?.retry().catch(() => undefined);
              }}
            />,
            document.body,
          )
        : null}

      {notice ? (
        <LiveUnavailableNotice
          reason={notice}
          onClose={() => setNotice(null)}
          onOpenModels={() => {
            setNotice(null);
            if (notice === "no_transcriber" && onOpenVoice) onOpenVoice();
            else onOpenModels();
          }}
        />
      ) : null}
    </>
  );
}

/** Encode captured bytes without a large argument list. / Кодує запис без великого списку аргументів. */
function audioBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}
